import React, { useEffect, useReducer, useState } from "react";
import { nanoid } from "nanoid";

import ProgressBar from "components/progressBar";
import LineLoader from "components/lineLoader";
import mergeStateReducer from "utils/mergeStateReducer";

import { API_FETCH_STATUS } from "constants/common";
import useFeedbackConfig from "components/feedback-framework/hooks/useFeedbackConfig";

import useFeedbackQuestionsState from "components/feedback-framework/hooks/useFeedbackQuestionsState";

import { QUESTION_TYPES, VIEW_TYPE_MAP } from "components/feedback-framework/constants/feedback.constants";
import { isValidAnswer } from "components/feedback-framework/utils/feedback.utils";

import FeedbackQuestions from "../feedback-questions/feedback-questions";
import FeedbackActions from "../feedback-actions/feedback-actions";
import { faOnFeedbackSubmit } from "../services/analytics.services";

const INITIAL_STATE = {
  questions: [],
  currentQuestionIndex: 0,
  progress: 0,
  previousQuestionProgress: 0,
};

const FeedbackComponent = ({ viewType, onSubmit, ratingSelected, isAuth }) => {
  const {
    feedbackConfig: {
      data: { questionnaire, conditionalSource },
      metaId,
    },
    saveFeedback,
    saveFeedbackStatus,
  } = useFeedbackConfig();
  const { questions: submittedQuestions, addQuestions, removeQuestion, getPayloadFromQuestions } = useFeedbackQuestionsState();
  const [state, setState] = useReducer(mergeStateReducer, INITIAL_STATE);
  const { questions, currentQuestionIndex, progress, previousQuestionProgress } = state;

  useEffect(() => {
    if (questionnaire?.length > 0) {
      const question = {
        id: nanoid(),
        ...questionnaire[0],
      };
      setState({ questions: [question] });
    }
  }, [questionnaire]);

  useEffect(() => {
    if (questions.length > 0 && ratingSelected) {
      let updatedRatingSelected;
      const { type, ratingRange } = questionnaire[0];
      if (type === QUESTION_TYPES.RATING) {
        for (let rating of ratingRange) {
          if (rating.value === ratingSelected) {
            updatedRatingSelected = rating;
          }
        }
      }
      onQuestionAnswered(questions[0]?.id, QUESTION_TYPES.RATING, updatedRatingSelected);
    }
  }, [questions[0]?.id]);

  const generateNewQuestion = (parentQuestionId, decisionConditional) => {
    return {
      id: nanoid(),
      ...conditionalSource[decisionConditional],
      decisionConditionalId: decisionConditional,
      parentQuestionId,
    };
  };

  const getUpdatedQuestionsList = (remainingQuestions, id, answer) => {
    const getDecisionConditonalQuestions = (id, answer) => {
      let questions = [];
      if (Array.isArray(answer)) {
        answer.forEach((ansOption) => {
          const { decisionConditional } = ansOption;
          if (decisionConditional) {
            const existingQuestionWithSameDecisionConditional = remainingQuestions.find(
              ({ decisionConditionalId }) => decisionConditionalId === decisionConditional,
            );
            if (existingQuestionWithSameDecisionConditional) {
              questions = [
                ...questions,
                existingQuestionWithSameDecisionConditional,
                ...getDecisionConditonalQuestions(
                  existingQuestionWithSameDecisionConditional.id,
                  existingQuestionWithSameDecisionConditional.answer,
                ),
              ];
            } else {
              questions.push(generateNewQuestion(id, decisionConditional));
            }
          }
        });
      } else if (answer) {
        const { decisionConditional } = answer;
        if (decisionConditional) {
          questions.push(generateNewQuestion(id, decisionConditional));
        }
      }
      return questions;
    };
    return [
      ...getDecisionConditonalQuestions(id, answer),
      ...remainingQuestions.filter(({ parentQuestionId }) => id !== parentQuestionId),
    ];
  };

  const onQuestionAnswered = (id, type, answer) => {
    const questionIndex = questions.findIndex(({ id: questionId }) => questionId === id);
    const isUpdatingQuestion = questionIndex <= questions.length - 1;
    let updatedQuestion = {
      ...questions[questionIndex],
      answer,
    };
    let newQuestions = [...questions.slice(0, questionIndex), updatedQuestion];
    newQuestions = [
      ...newQuestions,
      ...getUpdatedQuestionsList(isUpdatingQuestion ? [] : questions.slice(questionIndex + 1), id, answer),
    ];
    setState({
      questions: newQuestions,
      progress:
        previousQuestionProgress +
        newQuestions.reduce((prevProgress, { weightage, answer }) => {
          return isValidAnswer(answer) ? prevProgress + weightage : prevProgress;
        }, 0),
    });
  };
  const onBackClick = async () => {
    if (submittedQuestions.length === 0) return;
    let question = await removeQuestion();

    const questionsToBeDisplayed = [];
    let previousQuestionProgress = questionnaire
      .slice(0, currentQuestionIndex - 1)
      .reduce((prevProgress, { totalWeightage }) => prevProgress + totalWeightage, 0);
    while (question.decisionConditionalId) {
      questionsToBeDisplayed.unshift(question);
      question = await removeQuestion();
    }
    if (question.inputType === QUESTION_TYPES.RATING) {
      const { weightage } = question;
      addQuestions([question]);
      previousQuestionProgress += weightage;
    } else {
      questionsToBeDisplayed.unshift(question);
    }
    setState({
      questions: questionsToBeDisplayed,
      currentQuestionIndex: currentQuestionIndex - 1,
      progress:
        previousQuestionProgress + questionsToBeDisplayed.reduce((prevProgress, { weightage }) => prevProgress + weightage, 0),
      previousQuestionProgress,
    });
  };

  const isNextValid = () =>
    !questions.some(
      ({ mandatory, answer }) =>
        mandatory && (!answer || ((Array.isArray(answer) || typeof answer === "string") && answer.length === 0)),
    );

  const onNextClick = () => {
    addQuestions(questions);

    const currentQuestionProgress = questionnaire
      .slice(0, currentQuestionIndex + 1)
      .reduce((prevProgress, { totalWeightage }) => prevProgress + totalWeightage, 0);
    setState({
      questions: [
        {
          id: nanoid(),
          ...questionnaire[currentQuestionIndex + 1],
        },
      ],
      currentQuestionIndex: currentQuestionIndex + 1,
      progress: currentQuestionProgress,
      previousQuestionProgress: currentQuestionProgress,
    });
  };

  const isSubmitValid = () => isNextValid() && currentQuestionIndex === questionnaire.length - 1;

  const handleOnSubmissionAnalytics = () => {
    const params = {};
    questions?.forEach((question) => {
      const { inputType, answer } = question;
      if (answer) {
        if (inputType === QUESTION_TYPES.RATING) {
          params.rating = answer?.name;
        } else if (inputType === QUESTION_TYPES.MULTI_SELECT) {
          let reasons = [];
          answer.forEach((obj) => {
            reasons.push(obj?.label);
          });
          params.reasons = reasons.join(", ");
        } else if (inputType === QUESTION_TYPES.SINGLE_SELECT) {
          params.reasons = answer?.label;
        } else if (inputType === QUESTION_TYPES.TEXT_AREA) {
          params.comment = answer;
        }
      }
    });
    faOnFeedbackSubmit({...params, isAuth});
  };

  const onSubmitClick = () => {
    setState({ progress: 100 });
    const payload = getPayloadFromQuestions(questions, metaId);
    handleOnSubmissionAnalytics();
    saveFeedback(payload);
    onSubmit(payload);
  };

  return (
    <>
      <LineLoader show={saveFeedbackStatus === API_FETCH_STATUS.LOADING} />
      {viewType === VIEW_TYPE_MAP.LARGE && <ProgressBar value={progress} />}
      {questions.length > 0 && <FeedbackQuestions questions={questions} onQuestionAnswered={onQuestionAnswered} />}
      <FeedbackActions
        onBack={currentQuestionIndex !== questionnaire.length - 1 ? onBackClick : null}
        onNext={currentQuestionIndex !== questionnaire.length - 1 ? onNextClick : null}
        isNextDisbled={!isNextValid()}
        onSubmit={currentQuestionIndex === questionnaire.length - 1 ? onSubmitClick : null}
        isSubmitDisabled={!isSubmitValid()}
      />
    </>
  );
};

export default FeedbackComponent;
